// cityExtractor.ts
import logger from './logger';

// Define interfaces for Google Maps address components
export interface GoogleMapsAddressComponent {
    long_name: string;
    short_name: string;
    types: string[];
}

export interface PlaceDetails {
    address_components?: GoogleMapsAddressComponent[];
    formatted_address?: string;
    name?: string;
    [key: string]: any;
}

/**
 * Extracts city name from Google Places API address components using a hierarchical approach.
 * Follows these rules:
 * 1. Use locality (city) if available
 * 2. Use postal_town (UK-specific) if locality isn't available
 * 3. If it's a sublocality (neighbourhood), find its parent locality
 * 4. Use administrative_area_level_2 as a last resort, but only if it's likely a city
 * 5. Fall back to parsing the formatted address intelligently
 * 
 * @param placeDetails - The place details object from Google Places API
 * @returns The extracted city name or null if none could be determined
 */
export const extractCityFromPlaceDetails = (placeDetails: PlaceDetails): string | null => {
    try {
        if (!placeDetails || !placeDetails.address_components) {
            logger.warn('No address components found in place details');
            return null;
        }

        const components = placeDetails.address_components;
        logger.info('Extracting city from address components:', JSON.stringify(components));
        
        // First try to find a 'locality' (city)
        const cityComponent = components.find(
            component => component.types && component.types.includes('locality')
        );
        
        if (cityComponent) {
            logger.info('Found city from locality:', cityComponent.long_name);
            return cityComponent.long_name;
        }
        
        // If locality not found, try postal_town (UK-specific)
        const postalTownComponent = components.find(
            component => component.types && component.types.includes('postal_town')
        );
        
        if (postalTownComponent) {
            logger.info('Found city from postal_town:', postalTownComponent.long_name);
            return postalTownComponent.long_name;
        }
        
        // If it's a sublocality, find its parent locality
        const sublocality = components.find(
            component => component.types && component.types.includes('sublocality_level_1')
        );
        
        if (sublocality) {
            // Look for the parent locality in the same response
            const parentLocality = components.find(
                component => component.types && component.types.includes('locality')
            );
            
            if (parentLocality) {
                logger.info('Found city from parent locality of sublocality:', parentLocality.long_name);
                return parentLocality.long_name;
            }
        }
        
        // Check for administrative_area_level_2 (counties/districts) as fallback
        // But first check if it might be a large city that's classified as admin area
        const adminArea = components.find(
            component => component.types && component.types.includes('administrative_area_level_2')
        );
        
        if (adminArea) {
            // Only use admin area if it doesn't look like a county
            const name = adminArea.long_name;
            const countyIndicators = [
                'County', 'District', 'Borough', 'Council', 'Province', 'Region', 'Parish', 'Division'
            ];
            
            const isLikelyCounty = countyIndicators.some(indicator => name.includes(indicator));
            
            if (!isLikelyCounty) {
                logger.info('Found city from administrative_area_level_2:', name);
                return name;
            } else {
                logger.info('Skipping administrative area that looks like a county:', name);
            }
        }
        
        // Last resort: intelligent parse from formatted address
        if (placeDetails.formatted_address) {
            return extractCityFromFormattedAddress(placeDetails.formatted_address);
        }
        
        logger.warn('Could not determine city from place details');
        return null;
    } catch (error) {
        logger.error('Error extracting city from place details:', error);
        return null;
    }
};

/**
 * Intelligently extracts city name from a formatted address string.
 * Handles cases with postal codes and tries to avoid mistaking postcodes for city names.
 * 
 * @param formattedAddress - The formatted address string from Google Places API
 * @returns The extracted city name or null if none could be determined
 */
export const extractCityFromFormattedAddress = (formattedAddress: string): string | null => {
    try {
        //logger.info('Extracting city from formatted address:', formattedAddress);
        
        // Split address into parts by comma
        const addressParts = formattedAddress.split(',').map(part => part.trim());
        //logger.info('Address parts:', addressParts);
        
        // UK postcodes pattern: letters, numbers, space, numbers, letters
        // e.g., SW1A 1AA, BN1 4JU, etc.
        const ukPostcodePattern = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/i;
        
        // If there are at least 3 parts (most addresses will have more)
        if (addressParts.length >= 3) {
            // Start with the second-to-last part, which is often the city in UK addresses
            // before the postcode+country part
            let cityIndex = addressParts.length - 2;
            let potentialCity = addressParts[cityIndex];
            
            // Check if it looks like a UK postcode
            if (ukPostcodePattern.test(potentialCity)) {
                // If it's a postcode, try the part before it
                cityIndex = cityIndex - 1;
                potentialCity = addressParts[cityIndex];
                //logger.info('Potential city contained a postcode, using part before it:', potentialCity);
            } else if (potentialCity.includes(' ')) {
                // Check if the city part includes a postcode (e.g., "Brighton BN1 4JU")
                const cityParts = potentialCity.split(' ');
                const lastParts = cityParts.slice(-2).join(' ');
                
                if (ukPostcodePattern.test(lastParts)) {
                    // If it contains a postcode, strip it out
                    potentialCity = cityParts.slice(0, -2).join(' ');
                    //logger.info('Removed postcode from city name:', potentialCity);
                } else {
                    // Strip any UK postcode that might be part of the city name
                    const postcodePart = cityParts.find(part => ukPostcodePattern.test(part));
                    if (postcodePart) {
                        potentialCity = cityParts.filter(part => part !== postcodePart).join(' ');
                        //logger.info('Removed postcode part from city name:', potentialCity);
                    }
                }
            }
            
            // Additional check to remove any remaining postcodes
            if (potentialCity) {
                // Remove any part that looks like a postcode
                const cityWords = potentialCity.split(' ');
                const filteredWords = cityWords.filter(word => !ukPostcodePattern.test(word));
                
                // Check if the last word starts with a pattern that might be a partial postcode
                const lastWord = filteredWords[filteredWords.length - 1];
                if (lastWord && /^[A-Z]{1,2}[0-9]/.test(lastWord)) {
                    filteredWords.pop();
                }
                
                potentialCity = filteredWords.join(' ');
            }
            
            // Final check for "and" in city names (e.g., "Brighton and Hove")
            // These are sometimes split across multiple address parts
            for (let i = cityIndex - 1; i >= 0; i--) {
                const previousPart = addressParts[i];
                if (/and\s*$/i.test(previousPart) || /^\s*and/i.test(potentialCity)) {
                    // Combine parts that might be a single city name split by commas
                    potentialCity = previousPart + ' ' + potentialCity;
                    //logger.info('Combined city parts due to "and" detection:', potentialCity);
                    break;
                }
            }
            
            return potentialCity;
        }
        
        // Simple fallback for very short addresses
        if (addressParts.length >= 2) {
            return addressParts[0]; // Just use the first part as a desperate fallback
        }
        
        //logger.warn('Could not determine city from formatted address');
        return null;
    } catch (error) {
        //logger.error('Error extracting city from formatted address:', error);
        return null;
    }
};

/**
 * Complete city extraction function that handles multiple input formats
 * and returns the most appropriate city name.
 * 
 * @param input - Place details object or formatted address string
 * @returns The extracted city name or null if none could be determined
 */
export const extractCity = (input: PlaceDetails | string | null): string | null => {
    if (!input) return null;
    
    try {
        // If input is a string, assume it's a formatted address
        if (typeof input === 'string') {
            return extractCityFromFormattedAddress(input);
        }
        
        // Otherwise, assume it's a place details object
        const result = extractCityFromPlaceDetails(input);
        
        // Final cleanup - ensure we don't have any postcodes included
        if (result) {
            // UK postcodes pattern
            const ukPostcodePattern = /[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}/i;
            
            // Remove any postcode from the result
            return result.replace(ukPostcodePattern, '').trim();
        }
        
        return result;
    } catch (error) {
        //logger.error('Error in extractCity:', error);
        return null;
    }
};

export default extractCity;